import { Fragment, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, type LineaRemito, type OrdenRemitible, type Remito, type RemitoEnLista } from '../api';
import { mensajeDeError, useApi, useDemorado, useEscape, VIVO } from '../lib/useApi';
import { useEsAdmin } from '../lib/sesion';
import { imprimirDocumento } from '../lib/imprimir';
import { Cambia, Buscador, Cargando, ChipBoton, filtrar, Icono, Numero, Paginador, PanelError, useConfirmar, usePaginado, Vacio } from '../components/ui';
import { abrir, aparecer, CURVA, fila, resorte, suave } from '../components/movimiento';
import {
  altoMinimo,
  aviso,
  botonPrimario,
  botonSecundario,
  c,
  campo,
  chip,
  encabezadoTabla,
  etiquetaCampo,
  fechaImpresa,
  filaTabla,
  hoyISO,
  mono,
  nota,
  subtitulo,
  tarjeta,
  HUSO,
} from '../theme';
import { sumarRenglones } from '../lib/renglonesRemito';
import HojaRemito, { LETRAS_PRODUCTO, LINEAS_OBSERVACIONES, lineasObservaciones, RENGLONES_MAX } from './remito/Hoja';

/**
 * Remitos de salida.
 *
 * El papel que acompaña la mercadería al camión. Se arma eligiendo órdenes de
 * trabajo ya despachadas —cada una es un renglón—, se completan el transporte,
 * las observaciones y las facturas, y sale impreso.
 *
 * Los renglones se proponen solos con lo que sabe la orden y se editan antes
 * de imprimir: cómo se bultea la mercadería («8 CAJONES») lo sabe el que la
 * cargó, no el sistema. Lo que se imprime es lo que queda guardado, y el
 * historial es lo que permite volver a imprimirlo tal cual salió.
 */


/**
 * Cómo viaja una orden entre «Órdenes despachadas» y «Qué sale»: la tarjeta y
 * el renglón comparten `layoutId` y Motion anima la caja de una a la otra.
 */
const idViaje = (ordenId: string) => `orden-${ordenId}`;

/**
 * Las copias que se imprimen. El triplicado es opcional porque casi nunca se
 * usa: sale destildado y hay que pedirlo, en vez de tirar una hoja por remito.
 */
const COPIAS_BASE = ['ORIGINAL', 'DUPLICADO'] as const;
const TRIPLICADO = 'TRIPLICADO';

/** «1,00»: dos decimales y coma, como en el formulario de siempre. */
const cantidadImpresa = (n: number) =>
  (Number.isFinite(n) ? n : 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type Borrador = {
  fecha: string;
  transporte: string;
  transporteCuit: string;
  transporteDomicilio: string;
  observaciones: string;
  facturas: string;
  /** Vacío: el siguiente automático. Escrito: el número que pidió administración. */
  numero: string;
  lineas: LineaRemito[];
};

const BORRADOR_VACIO = (): Borrador => ({
  fecha: hoyISO(),
  transporte: '',
  transporteCuit: '',
  transporteDomicilio: '',
  observaciones: '',
  facturas: '',
  numero: '',
  lineas: [],
});

/**
 * El número escrito a mano como lo va a guardar el servidor («1234» →
 * «0009-00001234»), para que la confirmación y la vista previa digan lo mismo
 * que el papel. Es la misma regla que `numeroManual` del backend, que es la
 * que manda.
 */
function numeroEscrito(texto: string, punto: string): string | null {
  const m = /^(?:n\s*[º°o.]?\s*)?(?:(\d{1,5})\s*-\s*)?0*(\d{1,8})$/i.exec(texto.trim());
  if (!m || Number(m[2]) <= 0) return null;
  const pv = m[1] ? String(Number(m[1])).padStart(4, '0') : punto;
  return Number(pv) > 0 ? `${pv}-${m[2]!.padStart(8, '0')}` : null;
}

/** Cómo se lee el número del formulario: «Nº 0009 - 00001234». */
export function numeroImpreso(id: string): string {
  const m = /^(\d{4,5})-(\d{8})$/.exec(id);
  return m ? `Nº ${m[1]} - ${m[2]}` : id;
}

/**
 * Las celdas de la tabla del historial, con las medidas de las filas de las
 * otras listas pero sin `display` en línea: el celular lo cambia por CSS
 * (`.wms-tarjetas`) y un estilo en línea le ganaría.
 */
const { display: _d1, alignItems: _a1, gap: _g1, ...celda } = filaTabla;
const { display: _d2, alignItems: _a2, gap: _g2, ...celdaTitulo } = encabezadoTabla;
const titulo = { ...celdaTitulo, textAlign: 'left' } as const;

/** Lo que se está por mandar a la impresora. El `n` deja reimprimir lo mismo. */
type Pedido = { remito: Remito; n: number };

/**
 * `destino` llega del link (`#/remitos/…`), desde la ficha de una orden: una
 * orden (`OT-…`) abre el remito nuevo con ella ya elegida, y un número de
 * remito abre el historial buscándolo.
 */
export default function Remitos({ destino = null }: { destino?: string | null }) {
  const ordenInicial = destino && /^OT-/i.test(destino) ? destino.toUpperCase() : null;
  const remitoInicial = destino && !ordenInicial ? destino : null;
  const [solapa, setSolapa] = useState<'nuevo' | 'historial'>(remitoInicial ? 'historial' : 'nuevo');
  useEffect(() => {
    if (destino) setSolapa(remitoInicial ? 'historial' : 'nuevo');
  }, [destino, remitoInicial]);
  // Destildado: casi nunca se usa, así que el que lo necesita lo pide. Al
  // revés serían dos hojas de más por remito, todos los días.
  const [conTriplicado, setConTriplicado] = useState(false);
  const [error, setError] = useState('');
  const [pedido, setPedido] = useState<Pedido | null>(null);
  /** El remito emitido que se está corrigiendo en el formulario. */
  const [aEditar, setAEditar] = useState<Remito | null>(null);
  /** «Nuevo» sigue montado al irse (conserva el borrador): se esconde recién cuando terminó de salir. */
  const [nuevoOculto, setNuevoOculto] = useState(false);

  const copias = conTriplicado ? [...COPIAS_BASE, TRIPLICADO] : [...COPIAS_BASE];

  /** Manda a la impresora las hojas que están dibujadas en el DOM. */
  useEffect(() => {
    if (!pedido) return;
    // Un cuadro de tiempo para que React termine de dibujar las hojas antes de
    // leerlas.
    const id = setTimeout(() => {
      const hojas = [...document.querySelectorAll('#wms-remito-copias > [data-copia]')].map((n) => n.innerHTML);
      const problema = imprimirDocumento(`Remito ${numeroImpreso(pedido.remito.id)}`, hojas);
      if (problema) setError(problema);
    }, 0);
    return () => clearTimeout(id);
  }, [pedido]);

  const imprimir = (remito: Remito) => setPedido((p) => ({ remito, n: (p?.n ?? 0) + 1 }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <div style={{ ...subtitulo, marginTop: 0 }}>
          El papel que viaja con el camión. Elegí las órdenes de trabajo que salen, completá el transporte y sale
          impreso en original y duplicado.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-[10px]">
        <div className="wms-tira" style={{ gap: 10 }}>
          <ChipBoton grupo="remitos-solapa" activo={solapa === 'nuevo'} onClick={() => setSolapa('nuevo')}>
            Nuevo remito
          </ChipBoton>
          <ChipBoton grupo="remitos-solapa" activo={solapa === 'historial'} onClick={() => setSolapa('historial')}>
            Historial
          </ChipBoton>
        </div>

        {/* El tilde vive acá arriba y no adentro del formulario: manda sobre lo
            que sale de la impresora en los dos lados, tanto al emitir como al
            reimprimir uno viejo. */}
        <label
          className="tablet:ml-auto"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            color: c.texto2,
            cursor: 'pointer',
          }}
        >
          <input type="checkbox" checked={conTriplicado} onChange={(e) => setConTriplicado(e.target.checked)} />
          Imprimir también el triplicado
          <span style={{ fontSize: 12, color: c.texto3 }}>
            (<Numero valor={copias.length} /> hojas)
          </span>
        </label>
      </div>

      {error && <PanelError mensaje={error} />}

      {/* El formulario nuevo se esconde y no se desmonta: pasar a mirar el
          historial no tiene que tirar el borrador a medio armar. */}
      {/* Como no se desmonta, no puede entrar con `key`: se anima por el
          estado. Escondido queda en 0 (la animación corre igual, aunque no se
          vea), y al volver sube a 1. */}
      {/* El contenido entra por el lado de la pestaña elegida: «Nuevo» está a la
          izquierda y «Historial» a la derecha. Lo que se va sale hacia su lado y,
          mientras tanto, no empuja a lo que entra (queda flotando encima). */}
      <div style={{ position: 'relative' }}>
        <motion.div
          hidden={solapa !== 'nuevo' && nuevoOculto}
          initial={false}
          animate={solapa === 'nuevo' ? { opacity: 1, x: 0 } : { opacity: 0, x: -28 }}
          transition={suave}
          onAnimationComplete={() => setNuevoOculto(solapa !== 'nuevo')}
          style={solapa !== 'nuevo' ? { position: 'absolute', top: 0, left: 0, right: 0, pointerEvents: 'none' } : undefined}
        >
          <Nuevo
            onError={setError}
            onImprimir={imprimir}
            aEditar={aEditar}
            onFinEdicion={() => setAEditar(null)}
            ordenInicial={ordenInicial}
          />
        </motion.div>
        <AnimatePresence mode="popLayout" initial={false}>
          {solapa === 'historial' && (
            <motion.div
              key="historial"
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 28, transition: { duration: 0.22, ease: CURVA } }}
              transition={suave}
            >
              <Historial
                busquedaInicial={remitoInicial ?? ''}
                onError={setError}
                onImprimir={imprimir}
                onEditar={(r) => {
                  setAEditar(r);
                  setSolapa('nuevo');
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Las copias, dibujadas a tamaño real y escondidas: de acá las copia la
          ventana de impresión. `display:none` no impide leer el `innerHTML`, y
          evita que el navegador las maquete de más. */}
      {pedido && (
        <div id="wms-remito-copias" style={{ display: 'none' }}>
          {copias.map((copia) => (
            <div key={copia} data-copia={copia}>
              <HojaRemito
                remito={pedido.remito}
                copia={copia}
                fecha={fechaImpresa(pedido.remito.fecha)}
                cantidades={pedido.remito.lineas.map((l) => cantidadImpresa(l.cantidad))}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------- remito nuevo

function Nuevo({
  onError,
  onImprimir,
  aEditar,
  onFinEdicion,
  ordenInicial,
}: {
  onError: (m: string) => void;
  onImprimir: (r: Remito) => void;
  /** Un remito ya emitido para corregir: el formulario se carga con él. */
  aEditar: Remito | null;
  onFinEdicion: () => void;
  /** La orden con la que se llega desde su ficha: entra sola, una vez. */
  ordenInicial: string | null;
}) {
  /** El número del remito que se está corrigiendo; null es uno nuevo. */
  const [editando, setEditando] = useState<string | null>(null);
  const datos = useApi(() => api.remitoNuevo(editando ?? undefined), [editando]);
  const esAdmin = useEsAdmin();
  const [borrador, setBorrador] = useState<Borrador>(BORRADOR_VACIO);
  const [buscar, setBuscar] = useState('');
  const [emitiendo, setEmitiendo] = useState(false);
  const [emitido, setEmitido] = useState<Remito | null>(null);
  const [confirmar, dialogo] = useConfirmar();

  const cambiar = (cambio: Partial<Borrador>) => setBorrador((b) => ({ ...b, ...cambio }));

  useEffect(() => {
    if (!aEditar) return;
    setEditando(aEditar.id);
    setEmitido(null);
    setBorrador({
      fecha: aEditar.fecha,
      transporte: aEditar.transporte,
      transporteCuit: aEditar.transporteCuit,
      transporteDomicilio: aEditar.transporteDomicilio,
      observaciones: aEditar.observaciones,
      facturas: aEditar.facturas,
      numero: '',
      lineas: aEditar.lineas.map((l) => ({ ...l })),
    });
  }, [aEditar]);

  const elegidas = new Set(borrador.lineas.flatMap((l) => l.ordenes));
  const ordenes = datos.datos?.ordenes ?? [];
  const candidatas = filtrar(
    ordenes.filter((o) => !elegidas.has(o.ordenId)),
    buscar,
    (o) => [o.ordenId, ...o.articulos, ...o.lineas.map((l) => l.producto)],
  );

  // Se elige la orden entera: entran todos sus artículos, un renglón cada uno.
  // El producto que ya está en el remito por otra orden no repite renglón: se
  // le suma la cantidad.
  // Las observaciones de la orden se suman a las del remito, para no
  // tipearlas de nuevo. Quedan editables.
  function sumar(o: OrdenRemitible) {
    setEmitido(null);
    const actuales = borrador.observaciones.trim();
    const sugeridas = o.observaciones && !actuales.includes(o.observaciones) ? o.observaciones : '';
    cambiar({
      lineas: sumarRenglones(borrador.lineas, o.lineas),
      observaciones: sugeridas ? (actuales ? `${actuales}\n${sugeridas}` : sugeridas) : borrador.observaciones,
    });
  }

  // La orden que viene de su ficha entra sola, una vez por llegada: si se la
  // saca a mano no vuelve a aparecer.
  const usada = useRef<string | null>(null);
  useEffect(() => {
    if (!ordenInicial || usada.current === ordenInicial || !datos.datos || editando) return;
    usada.current = ordenInicial;
    if (elegidas.has(ordenInicial)) return;
    const o = ordenes.find((x) => x.ordenId.toUpperCase() === ordenInicial);
    if (o) sumar(o);
    else onError(`La ${ordenInicial} no se puede remitir: ya viajó en otro remito o todavía no tiene nada entregado.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordenInicial, datos.datos]);

  const editarLinea = (i: number, cambioL: Partial<LineaRemito>) =>
    cambiar({ lineas: borrador.lineas.map((l, n) => (n === i ? { ...l, ...cambioL } : l)) });

  const quitar = (i: number) => cambiar({ lineas: borrador.lineas.filter((_, n) => n !== i) });

  const conocido = (nombre: string) => datos.datos?.transportes.find((x) => x.nombre === nombre);

  function elegirTransporte(nombre: string) {
    const t = conocido(nombre);
    if (t) cambiar({ transporte: t.nombre, transporteCuit: t.cuit, transporteDomicilio: t.domicilio });
    // Si venía de uno conocido y el nombre dejó de coincidir, el CUIT y el
    // domicilio son del otro: se vacían para que no salgan impresos con un
    // nombre ajeno.
    else if (conocido(borrador.transporte)) cambiar({ transporte: nombre, transporteCuit: '', transporteDomicilio: '' });
    else cambiar({ transporte: nombre });
  }

  /**
   * El remito que se ve. Sale del borrador y no del servidor: es lo que hay en
   * pantalla, y así la vista previa no miente sobre lo que va a salir.
   */
  const manual = borrador.numero.trim() && datos.datos ? numeroEscrito(borrador.numero, datos.datos.puntoDeVenta) : null;
  const previa: Remito | null = datos.datos
    ? {
        id: editando ?? emitido?.id ?? manual ?? datos.datos.siguiente,
        fecha: borrador.fecha,
        transporte: borrador.transporte,
        transporteCuit: borrador.transporteCuit,
        transporteDomicilio: borrador.transporteDomicilio,
        observaciones: borrador.observaciones,
        facturas: borrador.facturas,
        emitidoPor: null,
        cliente: datos.datos.cliente,
        lineas: borrador.lineas,
      }
    : null;

  /** Corrige el remito emitido y lo reimprime: el papel nuevo reemplaza al viejo. */
  async function guardarEdicion(id: string) {
    const ok = await confirmar({
      titulo: `¿Guardar los cambios en el remito ${numeroImpreso(id)}?`,
      detalle: 'El número no cambia. Queda anotado en el historial del remito quién lo cambió, cuándo y qué.',
      boton: 'Guardar e imprimir',
    });
    if (!ok) return;
    setEmitiendo(true);
    onError('');
    try {
      const { cambios } = await api.editarRemito(id, borrador);
      if (cambios.length === 0) {
        onError('No había nada que cambiar: el remito quedó como estaba.');
        return;
      }
      const guardado = await api.remito(id);
      setEmitido(guardado);
      onImprimir(guardado);
      datos.recargar();
    } catch (err) {
      onError(mensajeDeError(err));
    } finally {
      setEmitiendo(false);
    }
  }

  async function emitir() {
    if (editando) return guardarEdicion(editando);
    // Cada emisión gasta un número que no se vuelve a usar: se pregunta antes.
    const n = borrador.lineas.length;
    const ok = await confirmar({
      titulo: '¿Emitir el remito?',
      detalle: (
        <>
          {manual ? (
            <>
              Sale con el número <strong>{numeroImpreso(manual)}</strong>, escrito a mano.
            </>
          ) : (
            <>
              Sale con el siguiente número automático, hoy <strong>{numeroImpreso(datos.datos?.siguiente ?? '')}</strong>.
            </>
          )}{' '}
          Transporte <strong>{borrador.transporte.trim()}</strong>, {n} {n === 1 ? 'renglón' : 'renglones'}. El
          número de remito queda usado; si hace falta, se da de baja desde el historial.
        </>
      ),
      boton: 'Emitir e imprimir el remito',
    });
    if (!ok) return;
    setEmitiendo(true);
    onError('');
    try {
      const { id } = await api.emitirRemito(borrador);
      // Se imprime lo que quedó guardado, no el borrador: si el servidor
      // corrigió algo, el papel tiene que decir lo mismo que la base.
      const guardado = await api.remito(id);
      setEmitido(guardado);
      onImprimir(guardado);
      datos.recargar();
    } catch (err) {
      onError(mensajeDeError(err));
    } finally {
      setEmitiendo(false);
    }
  }

  function limpiar() {
    setBorrador(BORRADOR_VACIO());
    setEmitido(null);
    setEditando(null);
    onFinEdicion();
    onError('');
  }

  if (datos.cargando && !datos.datos) return <Cargando texto="Buscando órdenes despachadas…" />;
  if (datos.error) return <PanelError mensaje={datos.error} onReintentar={datos.recargar} />;

  // `!(x > 0)` agarra también el campo vacío, que queda como NaN.
  const cantidadMala = (l: LineaRemito) => !(l.cantidad > 0);
  const sinCantidad = borrador.lineas.flatMap((l, i) => (cantidadMala(l) ? [i + 1] : []));
  const demasiados = borrador.lineas.length > RENGLONES_MAX;
  const obsLargas = lineasObservaciones(borrador.observaciones) > LINEAS_OBSERVACIONES;
  /** Lo que impide emitir, dicho para que se sepa qué arreglar. */
  const bloqueos = [
    ...(sinCantidad.length
      ? [`Falta la cantidad (o es 0) en ${sinCantidad.length === 1 ? 'el renglón' : 'los renglones'} ${sinCantidad.join(', ')}.`]
      : []),
    ...(demasiados ? [`El remito entra hasta ${RENGLONES_MAX} renglones; partilo en dos.`] : []),
    ...(!editando && borrador.numero.trim() && !manual
      ? [`«${borrador.numero.trim()}» no es un número de remito: escribilo como ${numeroImpreso(datos.datos?.siguiente ?? '')}, o dejalo vacío.`]
      : []),
  ];

  const listo = borrador.lineas.length > 0 && Boolean(borrador.transporte.trim()) && bloqueos.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {dialogo}
      <AnimatePresence>
      {emitido && (
        <motion.div key="emitido" {...abrir(16)} role="status" style={{ ...aviso('ok'), display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <Icono nombre="task_alt" size={24} color={c.verde} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>
              Remito {numeroImpreso(emitido.id)} {editando ? 'actualizado' : 'emitido'}
            </div>
            <div style={{ marginTop: 2, fontSize: 13, color: c.texto2 }}>
              Quedó en el historial: se puede volver a imprimir las veces que haga falta.
            </div>
          </div>
          <button
            type="button"
            onClick={() => onImprimir(emitido)}
            style={{ ...botonSecundario, padding: '9px 16px' }}
          >
            <Icono nombre="print" size={18} color={c.texto3} />
            Imprimir de nuevo
          </button>
          <button type="button" onClick={limpiar} style={{ ...botonSecundario, padding: '9px 16px' }}>
            Otro remito
          </button>
        </motion.div>
      )}

      {editando && !emitido && (
        <motion.div key="editando" {...abrir(16)} role="status" style={{ ...aviso('warn'), display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <Icono nombre="edit_note" size={24} color={c.ambar} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Editando el remito {numeroImpreso(editando)}</div>
            <div style={{ marginTop: 2, fontSize: 13, color: c.texto2 }}>
              Podés sumar o sacar órdenes y corregir cualquier dato. El número no cambia, y el cambio queda en su
              historial.
            </div>
          </div>
          <button type="button" onClick={limpiar} style={{ ...botonSecundario, padding: '9px 16px' }}>
            Cancelar la edición
          </button>
        </motion.div>
      )}
      </AnimatePresence>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(340px,100%),1fr))', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* ------------------------------------------------- renglones */}
          <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 14, position: 'relative' }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>
              Qué sale{' '}
              {borrador.lineas.length > 0 && (
                <>
                  · <Numero valor={borrador.lineas.length} />
                </>
              )}
            </span>

            <AnimatePresence initial={false}>
              {demasiados && (
                <motion.div key="demasiados" {...abrir(16)} style={aviso('warn')}>
                  El remito entra hasta {RENGLONES_MAX} renglones y tiene <Numero valor={borrador.lineas.length} />; partilo
                  en dos.
                </motion.div>
              )}

              {borrador.lineas.length === 0 && (
                <motion.div key="vacio" {...aparecer} style={nota}>
                  Todavía no elegiste ninguna orden. Buscá abajo y agregalas.
                </motion.div>
              )}
            </AnimatePresence>
            {/* La clave es la orden más cuántas veces se repite antes (una orden
                con varios artículos son varios renglones): no depende del
                código, que se edita, ni del lugar, que cambia al sacar uno. */}
            <AnimatePresence initial={false} mode="popLayout">
              {borrador.lineas.map((l, i) => {
                const primera = l.ordenes[0] ?? null;
                const repetida = borrador.lineas.slice(0, i).filter((x) => (x.ordenes[0] ?? null) === primera).length;
                // Sólo el primer renglón de la orden lleva el `layoutId`: es el que
                // viaja desde (y vuelve a) la tarjeta de «Órdenes despachadas».
                const viaja = repetida === 0 && primera !== null;
                return (
                <motion.div
                  key={`${primera}#${repetida}`}
                  layout
                  layoutId={viaja ? idViaje(primera!) : undefined}
                  {...(viaja
                    ? {
                        initial: { opacity: 0 },
                        animate: { opacity: 1 },
                        exit: { opacity: 0, transition: { duration: 0.2, ease: CURVA } },
                        transition: { layout: resorte, opacity: { duration: 0.25, ease: CURVA } },
                      }
                    : fila(i))}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    padding: 12,
                    border: `1px solid ${cantidadMala(l) ? c.rojo : i >= RENGLONES_MAX ? c.ambar : c.borde}`,
                    background: cantidadMala(l) ? c.rojoFila : undefined,
                    borderRadius: 16,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontFamily: mono, fontSize: 12.5, color: c.texto3, flex: 1 }}>
                      {i + 1}. {l.ordenes.join(' + ')}
                    </span>
                    <button
                      type="button"
                      onClick={() => quitar(i)}
                      title="Sacar este renglón"
                      style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', display: 'grid' }}
                    >
                      <Icono nombre="close" size={17} color={c.texto3} />
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <label style={etiquetaCampo}>
                      Código
                      <input
                        value={l.codigo}
                        onChange={(e) => editarLinea(i, { codigo: e.target.value })}
                        style={{ ...campo, fontFamily: mono }}
                      />
                    </label>
                    <label style={etiquetaCampo}>
                      Cantidad
                      <input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="0.01"
                        value={Number.isNaN(l.cantidad) ? '' : l.cantidad}
                        // Vacío queda vacío (NaN) y no 0: si no, se imprime «0,00».
                        onChange={(e) => editarLinea(i, { cantidad: e.target.value === '' ? NaN : Number(e.target.value) })}
                        style={{ ...campo, borderColor: cantidadMala(l) ? c.rojo : undefined }}
                      />
                    </label>
                  </div>
                  <label style={etiquetaCampo}>
                    Producto
                    <input
                      value={l.producto}
                      onChange={(e) => editarLinea(i, { producto: e.target.value })}
                      style={campo}
                    />
                    <AnimatePresence initial={false}>
                      {l.producto.length > LETRAS_PRODUCTO && (
                        <motion.span key="largo" {...aparecer} style={{ display: 'block', fontSize: 12, color: c.ambar, fontWeight: 400 }}>
                          Es largo: en el papel se corta cerca de la letra {LETRAS_PRODUCTO}.
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </label>
                </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* ------------------------------------- órdenes para agregar */}
          <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>Órdenes despachadas</span>
            <div style={nota}>
              Sólo las que ya tienen mercadería entregada y todavía no salieron en ningún remito. Para volver a
              imprimir uno que ya se emitió, está el historial.
            </div>
            {/* En una columna el `flex: 1` del buscador le sacaría el alto. */}
            <div className="flex">
              <Buscador valor={buscar} onCambiar={setBuscar} placeholder="Orden, artículo o producto" />
            </div>

            {candidatas.length === 0 ? (
              <Vacio
                icono="assignment_turned_in"
                titulo={ordenes.length === 0 ? 'No hay órdenes despachadas' : 'No queda ninguna sin agregar'}
                detalle={
                  ordenes.length === 0
                    ? 'Una orden entra al remito cuando el depósito entregó al menos una de sus salidas, y sale de la lista cuando ya viajó en uno.'
                    : 'Las que había ya están en este remito, o no coinciden con la búsqueda.'
                }
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflowY: 'auto', position: 'relative' }}>
                <AnimatePresence initial={false} mode="popLayout">
                {candidatas.map((o, i) => (
                  <motion.button
                    key={o.ordenId}
                    layout
                    layoutId={idViaje(o.ordenId)}
                    {...fila(i)}
                    // Al agregarla viaja a «Qué sale»: acá sólo se desvanece.
                    exit={{ opacity: 0, transition: { duration: 0.2, ease: CURVA } }}
                    transition={{ ...fila(i).transition, layout: resorte }}
                    type="button"
                    onClick={() => sumar(o)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      border: `1px solid ${c.borde}`,
                      borderRadius: 12,
                      background: c.panel,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <Icono nombre="add" size={18} color={c.texto3} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {o.lineas.map((l, n) => (
                        <span key={n} style={{ display: 'block', fontSize: 13.5, color: c.texto }}>
                          {l.producto}
                        </span>
                      ))}
                      <span style={{ display: 'block', fontSize: 12, color: c.texto3, fontFamily: mono }}>
                        {o.ordenId} · {o.entregadas} de {o.salidas} entregadas
                        {o.remitidas > 0 && ` · ${o.remitidas} ya en otro remito`}
                      </span>
                    </span>
                  </motion.button>
                ))}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* --------------------------------- transporte, obs y facturas */}
          <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>Transporte</span>

            <label style={etiquetaCampo}>
              Nombre / Razón Social
              <input
                value={borrador.transporte}
                onChange={(e) => elegirTransporte(e.target.value)}
                list="wms-transportes"
                style={campo}
              />
              <span style={{ fontSize: 12, color: c.texto3, fontWeight: 400 }}>
                Los que ya se usaron aparecen en la lista y traen su CUIT y domicilio.
              </span>
            </label>
            <datalist id="wms-transportes">
              {(datos.datos?.transportes ?? []).map((t) => (
                <option key={t.nombre} value={t.nombre} />
              ))}
            </datalist>

            <div className="grid grid-cols-1 gap-[10px] tablet:grid-cols-2">
              <label style={etiquetaCampo}>
                CUIT
                <input
                  value={borrador.transporteCuit}
                  onChange={(e) => cambiar({ transporteCuit: e.target.value })}
                  style={{ ...campo, fontFamily: mono }}
                />
              </label>
              <label style={etiquetaCampo}>
                Fecha de emisión
                <input
                  type="date"
                  value={borrador.fecha}
                  onChange={(e) => cambiar({ fecha: e.target.value || hoyISO() })}
                  style={campo}
                />
              </label>
            </div>

            <label style={etiquetaCampo}>
              Domicilio Comercial
              <input
                value={borrador.transporteDomicilio}
                onChange={(e) => cambiar({ transporteDomicilio: e.target.value })}
                style={campo}
              />
            </label>

            <label style={etiquetaCampo}>
              Observaciones
              <textarea
                rows={3}
                value={borrador.observaciones}
                onChange={(e) => cambiar({ observaciones: e.target.value })}
                style={{
                  padding: '12px 14px',
                  border: `1px solid ${c.bordeFuerte}`,
                  borderRadius: 16,
                  background: c.panel,
                  fontSize: 13.5,
                  color: c.texto,
                  resize: 'vertical',
                }}
              />
              <AnimatePresence initial={false}>
                {obsLargas && (
                  <motion.span key="obs" {...aparecer} style={{ display: 'block', fontSize: 12, color: c.ambar, fontWeight: 400 }}>
                    En el recuadro entran {LINEAS_OBSERVACIONES} renglones: lo que sigue se monta sobre las facturas.
                  </motion.span>
                )}
              </AnimatePresence>
            </label>

            <label style={etiquetaCampo}>
              Facturas Nº
              <input value={borrador.facturas} onChange={(e) => cambiar({ facturas: e.target.value })} style={campo} />
            </label>

            {/* Casi siempre vacío: el sistema pone el siguiente. Está para el
                remito que administración necesita con un número puntual. Al
                editar no va: el número de un remito emitido no cambia. */}
            {!editando && (
            <label style={etiquetaCampo}>
              Número de remito
              <input
                value={borrador.numero}
                onChange={(e) => cambiar({ numero: e.target.value })}
                placeholder={`Automático: ${numeroImpreso(datos.datos?.siguiente ?? '')}`}
                style={{ ...campo, fontFamily: mono, borderColor: borrador.numero.trim() && !manual ? c.rojo : undefined }}
              />
              <span style={{ fontSize: 12, color: c.texto3, fontWeight: 400 }}>
                Dejalo vacío para que salga el siguiente. Si escribís uno (Nº 0009 - 00001234, o sólo el número), no puede
                estar ya usado.
              </span>
            </label>
            )}
            {!editando && esAdmin && datos.datos && (
              <PuntoDeVenta actual={datos.datos.puntoDeVenta} onCambio={datos.recargar} />
            )}

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={emitir}
                disabled={emitiendo || !listo}
                className="flex-1 tablet:flex-none"
                style={{ ...botonPrimario, opacity: emitiendo || !listo ? 0.6 : 1 }}
              >
                <Cambia clave={emitiendo ? 'guardando' : editando ? 'guardar' : 'emitir'}>
                  {emitiendo ? 'Guardando…' : editando ? 'Guardar e imprimir' : 'Emitir e imprimir'}
                </Cambia>
              </button>
              <button type="button" onClick={limpiar} className="flex-1 tablet:flex-none" style={botonSecundario}>
                Limpiar
              </button>
            </div>
            <AnimatePresence initial={false}>
              {bloqueos.map((b) => (
                <motion.div key={b} {...aparecer} style={{ fontSize: 13, color: c.rojo }}>
                  {b}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {previa && <Previa remito={previa} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- historial

/**
 * Los remitos que ya se emitieron.
 *
 * Existe para volver a imprimir: una hoja que se traba en la impresora, un
 * camión que se fue sin el papel, una copia que pidió administración. Se
 * reimprime **lo que quedó guardado**, no un remito armado de nuevo, así que
 * el papel dice lo mismo que el primero.
 */
function Historial({
  busquedaInicial,
  onError,
  onImprimir,
  onEditar,
}: {
  /** El remito con el que se llega desde la ficha de una orden. */
  busquedaInicial: string;
  onError: (m: string) => void;
  onImprimir: (r: Remito) => void;
  onEditar: (r: Remito) => void;
}) {
  const [texto, setTexto] = useState(busquedaInicial);
  const [trayendo, setTrayendo] = useState<string | null>(null);
  /** El remito con su historial abierto, y lo que trajo. */
  const [abierto, setAbierto] = useState<Remito | null>(null);

  // Se consulta al servidor al dejar de tipear, no en cada tecla.
  const q = useDemorado(texto);

  const { datos, cargando, error, recargar } = useApi(() => api.remitos(q), [q], VIVO.normal);
  const pagina = usePaginado(datos ?? []);

  /** Trae el remito guardado y hace algo con él: imprimirlo, editarlo o ver su historial. */
  async function traer(r: RemitoEnLista, hacer: (completo: Remito) => void) {
    setTrayendo(r.id);
    onError('');
    try {
      hacer(await api.remito(r.id));
    } catch (err) {
      onError(mensajeDeError(err));
    } finally {
      setTrayendo(null);
    }
  }
  const reimprimir = (r: RemitoEnLista) => traer(r, onImprimir);
  const [confirmar, dialogo] = useConfirmar();

  async function darDeBaja(r: RemitoEnLista) {
    const motivo = await confirmar({
      titulo: `¿Dar de baja el remito ${numeroImpreso(r.id)}?`,
      detalle:
        'Queda guardado en el historial, marcado como dado de baja, y su número no se vuelve a usar. Sus órdenes vuelven a poder entrar en un remito nuevo.',
      boton: 'Dar de baja el remito',
      motivo: 'Motivo',
    });
    if (!motivo) return;
    setTrayendo(r.id);
    onError('');
    try {
      await api.darDeBajaRemito(r.id, motivo);
      setAbierto(null);
      recargar();
    } catch (err) {
      onError(mensajeDeError(err));
    } finally {
      setTrayendo(null);
    }
  }

  if (cargando && !datos) return <Cargando texto="Buscando remitos…" />;
  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {dialogo}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Buscador valor={texto} onCambiar={setTexto} placeholder="Remito, transporte o factura" />
        <span style={{ fontSize: 12.5, color: c.texto3 }}>
          <Numero valor={datos?.length ?? 0} desdeCero={false} /> {datos?.length === 1 ? 'remito' : 'remitos'}
        </span>
      </div>

      <div style={{ ...tarjeta, overflow: 'hidden' }}>
        <div className="wms-scroll-x">
          <table className="wms-tarjetas" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: c.fondo, borderBottom: `1px solid ${c.borde}` }}>
                <th style={{ ...titulo, width: 140 }}>Remito</th>
                <th style={{ ...titulo, width: 100 }}>Fecha</th>
                <th style={{ ...titulo, minWidth: 180, width: '24%' }}>Transporte</th>
                <th style={{ ...titulo, minWidth: 140 }}>Facturas</th>
                <th style={{ ...titulo, width: 90, textAlign: 'right' }}>Renglones</th>
                <th style={{ ...titulo, minWidth: 140 }}>Emitió</th>
                <th style={titulo} />
              </tr>
            </thead>
            <tbody>
            {pagina.visibles.map((r, n) => (
              <Fragment key={r.id}>
              <motion.tr {...fila(n)} layout="position" animate={{ opacity: r.anuladoEn ? 0.75 : 1, y: 0 }}>
                <td className="wms-principal" style={{ ...celda, fontFamily: mono, fontSize: 12.5 }}>
                  <span style={{ display: 'block', textDecoration: r.anuladoEn ? 'line-through' : undefined }}>
                    {numeroImpreso(r.id)}
                  </span>
                  {r.anuladoEn && (
                    <span
                      title={`Dado de baja por ${r.anuladoPor ?? '—'} el ${new Date(r.anuladoEn).toLocaleString('es-AR', { timeZone: HUSO, dateStyle: 'short', timeStyle: 'short' })}`}
                      style={{ ...chip('err'), fontFamily: 'inherit', fontSize: 12, padding: '2px 8px', marginTop: 4 }}
                    >
                      Dado de baja
                    </span>
                  )}
                  {/* El que no salió de la secuencia se ve: explica un número
                      fuera de orden en la lista. */}
                  {r.numeroManual && (
                    <span style={{ ...chip('neu'), fontFamily: 'inherit', fontSize: 12, padding: '2px 8px', marginTop: 4 }}>
                      Número manual
                    </span>
                  )}
                </td>
                <td data-rotulo="Fecha" style={{ ...celda, color: c.texto2, fontSize: 13 }}>{fechaImpresa(r.fecha)}</td>
                <td
                  data-rotulo="Transporte"
                  style={{
                    ...celda,
                    color: c.texto2,
                    maxWidth: 260,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.transporte || '—'}
                </td>
                <td data-rotulo="Facturas" style={{ ...celda, color: c.texto2, fontSize: 13 }}>{r.facturas || '—'}</td>
                <td
                  data-rotulo="Renglones"
                  style={{ ...celda, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: c.texto3, fontSize: 12.5 }}
                >
                  {r.lineas}
                </td>
                <td data-rotulo="Emitió" style={{ ...celda, color: c.texto3, fontSize: 12.5 }}>{r.emitidoPor ?? '—'}</td>
                <td className="wms-acciones" style={celda}>
                  <span className="flex flex-wrap justify-start gap-2 tablet:flex-nowrap tablet:justify-end">
                    <button
                      type="button"
                      onClick={() => (abierto?.id === r.id ? setAbierto(null) : traer(r, setAbierto))}
                      disabled={trayendo === r.id}
                      title="Quién lo emitió y qué se le cambió"
                      style={{ ...botonSecundario, minHeight: altoMinimo(34), padding: '6px 12px' }}
                    >
                      <Icono nombre="history" size={16} color={c.texto3} />
                      {r.ediciones > 0 ? `${r.ediciones} ${r.ediciones === 1 ? 'cambio' : 'cambios'}` : 'Historial'}
                    </button>
                    {/* Uno dado de baja ya no vale: no se corrige ni se vuelve a imprimir. */}
                    {!r.anuladoEn && (
                      <>
                        <button
                          type="button"
                          onClick={() => darDeBaja(r)}
                          disabled={trayendo === r.id}
                          title="Anularlo: queda guardado, marcado como dado de baja"
                          style={{ ...botonSecundario, minHeight: altoMinimo(34), padding: '6px 12px', color: c.rojo }}
                        >
                          <Icono nombre="block" size={16} color={c.rojo} />
                          Dar de baja
                        </button>
                        <button
                          type="button"
                          onClick={() => traer(r, onEditar)}
                          disabled={trayendo === r.id}
                          style={{ ...botonSecundario, minHeight: altoMinimo(34), padding: '6px 12px' }}
                        >
                          <Icono nombre="edit" size={16} color={c.texto3} />
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => reimprimir(r)}
                          disabled={trayendo === r.id}
                          style={{ ...botonSecundario, minHeight: altoMinimo(34), padding: '6px 12px', opacity: trayendo === r.id ? 0.6 : 1 }}
                        >
                          <Icono nombre="print" size={16} color={c.texto3} />
                          <Cambia clave={trayendo === r.id ? 'abriendo' : 'imprimir'}>
                            {trayendo === r.id ? 'Abriendo…' : 'Imprimir'}
                          </Cambia>
                        </button>
                      </>
                    )}
                  </span>
                </td>
              </motion.tr>
              <AnimatePresence initial={false}>
                {abierto?.id === r.id && (
                  <motion.tr key="cambios" {...aparecer}>
                    <td colSpan={7} style={{ ...celda, padding: 0 }}>
                      <div
                        className="pt-1 pr-4 pb-[14px] pl-4"
                        style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0 }}
                      >
                        {(abierto.cambios ?? []).map((x, i) => (
                          <div key={i} style={{ display: 'flex', gap: 12, fontSize: 13, alignItems: 'baseline', flexWrap: 'wrap' }}>
                            <span style={{ color: c.texto3, fontVariantNumeric: 'tabular-nums', minWidth: 140 }}>
                              {new Date(x.en).toLocaleString('es-AR', { timeZone: HUSO, dateStyle: 'short', timeStyle: 'short' })}
                            </span>
                            <span style={{ color: c.texto3, minWidth: 120 }}>{x.por ?? '—'}</span>
                            <span style={{ color: c.texto, flex: 1, minWidth: 220 }}>{x.detalle}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </motion.tr>
                )}
              </AnimatePresence>
              </Fragment>
            ))}
            </tbody>
          </table>
        </div>

        <Paginador
          pagina={pagina.pagina}
          paginas={pagina.paginas}
          total={pagina.total}
          desde={pagina.desde}
          que="remitos"
          onIr={pagina.setPagina}
        />

        {(datos?.length ?? 0) === 0 && (
          <Vacio
            icono={q ? 'search_off' : 'receipt_long'}
            titulo={q ? 'Sin resultados' : 'Todavía no se emitió ningún remito'}
            detalle={
              q
                ? 'Probá con el número de remito, el transporte o el número de factura.'
                : 'El primero se emite desde «Nuevo remito», eligiendo las órdenes que salen.'
            }
          />
        )}
      </div>
    </div>
  );
}

/** La hoja a escala: la misma que se imprime, sólo cambia el envoltorio. */
function HojaEscalada({ remito, escala, layoutId }: { remito: Remito; escala: number; layoutId?: string }) {
  return (
    <motion.div
      layoutId={layoutId}
      transition={{ layout: { duration: 0.5, ease: CURVA } }}
      style={{
        width: `${210 * escala}mm`,
        height: `${297 * escala}mm`,
        overflow: 'hidden',
        border: `1px solid ${c.borde}`,
        borderRadius: 4,
        background: '#fff',
      }}
    >
      <div style={{ transform: `scale(${escala})`, transformOrigin: 'top left' }}>
        <HojaRemito
          remito={remito}
          copia={COPIAS_BASE[0]}
          fecha={fechaImpresa(remito.fecha)}
          cantidades={remito.lineas.map((l) => cantidadImpresa(l.cantidad))}
        />
      </div>
    </motion.div>
  );
}

/**
 * La hoja como va a salir, achicada para que entre al costado del formulario.
 * Así no se llega a leer: «Ver en grande» la abre a todo el ancho de la
 * pantalla.
 */
function Previa({ remito }: { remito: Remito }) {
  const [grande, setGrande] = useState(false);
  useEscape(grande, () => setGrande(false));
  // 210 mm son unos 794 px: entra a lo ancho de la pantalla, sin pasar de
  // una vez y media el tamaño real.
  const escalaGrande = Math.min(1.5, (window.innerWidth - 64) / 794);
  // La miniatura, en el celular, achicada hasta entrar en la tarjeta (16 de
  // margen y 16 de relleno a cada lado) en vez de scrollear de costado.
  const escalaChica = Math.min(0.62, (window.innerWidth - 66) / 794);
  return (
    <div style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: c.texto }}>Cómo sale</span>
        <button type="button" onClick={() => setGrande(true)} style={{ ...botonSecundario, padding: '6px 12px', fontSize: 12.5 }}>
          <Icono nombre="open_in_full" size={16} />
          Ver en grande
        </button>
      </div>
      <button
        type="button"
        onClick={() => setGrande(true)}
        title="Ver en grande"
        style={{ overflow: 'auto', border: 0, padding: 0, background: 'none', cursor: 'zoom-in', textAlign: 'left' }}
      >
        <HojaEscalada remito={remito} escala={escalaChica} layoutId="hoja-remito" />
      </button>

      <AnimatePresence>
      {grande && (
        <motion.div
          key="grande"
          // El fondo sólo se aclara: la hoja es la que viaja desde la miniatura.
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.28, ease: CURVA } }}
          transition={{ duration: 0.25 }}
          role="dialog"
          aria-modal="true"
          aria-label="Vista previa del remito"
          onClick={() => setGrande(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            background: 'rgba(26,26,26,.6)',
            overflow: 'auto',
            padding: '16px 0 32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>Remito {numeroImpreso(remito.id)}</span>
            <button type="button" onClick={() => setGrande(false)} style={{ ...botonSecundario, padding: '6px 12px', fontSize: 12.5 }}>
              <Icono nombre="close" size={16} />
              Cerrar
            </button>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <HojaEscalada remito={remito} escala={escalaGrande} layoutId="hoja-remito" />
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}

/**
 * El punto de venta de los remitos nuevos (el «0009» de Nº 0009 - 00001234).
 * Cuando pasan a otro, se cambia acá y el nuevo numera desde 00000001; los ya
 * emitidos no se tocan. Sólo administración.
 */
function PuntoDeVenta({ actual, onCambio }: { actual: string; onCambio: () => void }) {
  const [texto, setTexto] = useState(actual);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [confirmar, dialogo] = useConfirmar();
  useEffect(() => setTexto(actual), [actual]);

  async function guardar() {
    const ok = await confirmar({
      titulo: `¿Pasar al punto de venta ${texto.trim().padStart(4, '0')}?`,
      detalle: 'Los remitos nuevos salen con ese punto de venta y su numeración arranca en 00000001. Los ya emitidos no cambian.',
      boton: 'Cambiar punto de venta',
    });
    if (!ok) return;
    setError('');
    setGuardando(true);
    try {
      await api.cambiarPuntoDeVenta(texto);
      onCambio();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setGuardando(false);
    }
  }

  const cambiado = texto.trim() !== '' && texto.trim().padStart(4, '0') !== actual;
  return (
    <label style={etiquetaCampo}>
      Punto de venta
      <span style={{ display: 'flex', gap: 8 }}>
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value.replace(/\D/g, '').slice(0, 5))}
          inputMode="numeric"
          style={{ ...campo, fontFamily: mono, width: 110 }}
        />
        <AnimatePresence initial={false}>
          {cambiado && (
            <motion.span key="cambiar" {...abrir(999)} style={{ display: 'flex' }}>
              <button type="button" onClick={guardar} disabled={guardando} style={{ ...botonSecundario, opacity: guardando ? 0.6 : 1 }}>
                <Cambia clave={guardando ? 'g' : 'c'}>{guardando ? 'Guardando…' : 'Cambiar'}</Cambia>
              </button>
            </motion.span>
          )}
        </AnimatePresence>
      </span>
      <span style={{ fontSize: 12, color: error ? c.rojo : c.texto3, fontWeight: 400 }}>
        {error || 'El de los remitos nuevos. Si pasan a otro, se cambia acá y numera desde 00000001.'}
      </span>
      {dialogo}
    </label>
  );
}
